import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Download, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const BulkImport = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setResult(null);
    } else {
      toast.error('Please select a valid CSV file');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/players/bulk-import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setResult(response.data);
      if (response.data.imported > 0) {
        toast.success(`Successfully imported ${response.data.imported} players!`);
      }
      if (response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import players');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = `name,age,position,nationality,current_club,contract_end_date,market_value,salary,agent_fee,stats_goals,stats_assists,stats_appearances,injury_history,performance_notes
John Doe,25,Forward,England,Manchester United,2026-06-30,50000000,3000000,150000,15,8,32,None,Excellent finishing ability
Jane Smith,28,Midfielder,Spain,Barcelona,2025-12-31,35000000,2500000,125000,5,12,30,Minor knee injury 2023,Great vision and passing`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'player_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="animate-fade-in">
      <div className="relative bg-black/60 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="hero-glow"></div>
        <div className="relative z-10 p-6 md:p-8 lg:p-12">
          <Link
            to="/players"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            <span className="uppercase tracking-wide text-sm">Back to Players</span>
          </Link>
          <h1 className="text-5xl md:text-6xl font-bold uppercase tracking-tight leading-none text-white mb-2">
            Bulk Import Players
          </h1>
          <p className="text-lg text-zinc-300">Upload a CSV file to import multiple players at once</p>
        </div>
      </div>

      <div className="p-6 md:p-8 lg:p-12">
        <div className="max-w-4xl mx-auto">
          {/* Instructions */}
          <div className="glass-card p-6 mb-6">
            <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4">How to Import</h2>
            <ol className="space-y-3 text-zinc-300">
              <li className="flex gap-3">
                <span className="text-white font-bold">1.</span>
                <span>Download the CSV template below to see the required format</span>
              </li>
              <li className="flex gap-3">
                <span className="text-white font-bold">2.</span>
                <span>Fill in your player data (name, age, and position are required)</span>
              </li>
              <li className="flex gap-3">
                <span className="text-white font-bold">3.</span>
                <span>Upload the completed CSV file using the form below</span>
              </li>
            </ol>
            <button
              onClick={downloadTemplate}
              className="mt-6 inline-flex items-center gap-2 bg-white/10 text-white hover:bg-white/20 border border-white/5 rounded-sm px-4 py-2 transition-all duration-200"
            >
              <Download className="w-4 h-4" strokeWidth={1.5} />
              Download CSV Template
            </button>
          </div>

          {/* Upload Form */}
          <div className="glass-card p-8">
            <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6">Upload File</h2>
            
            <div className="border-2 border-dashed border-white/10 rounded-lg p-12 text-center hover:border-[#FFFFFF] transition-all duration-200">
              <Upload className="w-12 h-12 text-zinc-500 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-lg text-white mb-2">Select CSV File</p>
              <p className="text-sm text-zinc-500 mb-4">or drag and drop here</p>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                id="csv-upload"
              />
              <label
                htmlFor="csv-upload"
                className="inline-block bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm px-6 py-3 cursor-pointer transition-all duration-200"
              >
                Choose File
              </label>
              {file && (
                <p className="mt-4 text-white font-medium">
                  Selected: {file.name}
                </p>
              )}
            </div>

            {file && (
              <button
                onClick={handleUpload}
                disabled={uploading}
                data-testid="upload-csv-button"
                className="w-full mt-6 inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm py-3 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Upload className="w-5 h-5" strokeWidth={2} />
                {uploading ? 'Uploading...' : 'Upload and Import'}
              </button>
            )}
          </div>

          {/* Results */}
          {result && (
            <div className="glass-card p-8 mt-6">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6">Import Results</h2>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-400" strokeWidth={1.5} />
                  <div>
                    <p className="text-lg font-bold text-white">{result.imported} Players Imported</p>
                    <p className="text-sm text-zinc-400">Successfully added to your roster</p>
                  </div>
                </div>

                {result.errors.length > 0 && (
                  <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <AlertCircle className="w-6 h-6 text-red-400" strokeWidth={1.5} />
                      <p className="text-lg font-bold text-white">{result.errors.length} Errors</p>
                    </div>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {result.errors.map((error, index) => (
                        <p key={index} className="text-sm text-red-300 font-mono">
                          {error}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => navigate('/players')}
                  className="w-full bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm py-3 transition-all duration-200"
                >
                  View All Players
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkImport;